'use client';

import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useExtendedBackfillMetricsStore } from '@/state/extendedBackfillMetricsState';
import { useOnboardingBackfillStore } from '@/state/onboardingBackfillState';
import { setTeamInsightsOnboardingComplete } from '@/apis/teamInsightsOnboarding';

/**
 * Syncs errors and completion from extendedBackfillMetrics (global backfill state) into
 * onboardingBackfillState when the running session is the one started from onboarding.
 * On success, calls setInternalOnboardingTeamInsights with done: 1.
 * Mounted in providers so it runs app-wide.
 */
export function OnboardingBackfillErrorSync() {
  // This component is mounted in providers, above the dashboard's auth gate, so
  // it also runs on /login. Both stores it reads are persisted to localStorage
  // and outlive a logout, so without this gate a completed onboarding backfill
  // keeps POSTing after sign-out and every one of those calls 401s.
  const { status } = useSession();
  const onboardingSessionId = useOnboardingBackfillStore((s) => s.sessionId);
  const onboardingOrgUuid = useOnboardingBackfillStore((s) => s.orgUuid);
  const setOnboardingError = useOnboardingBackfillStore((s) => s.setError);
  const markOnboardingCompleted = useOnboardingBackfillStore(
    (s) => s.markCompleted
  );
  const extendedError = useExtendedBackfillMetricsStore((s) => s.error);
  const extendedSessionId = useExtendedBackfillMetricsStore((s) => s.sessionId);
  const extendedIsCompleted = useExtendedBackfillMetricsStore(
    (s) => s.isCompleted
  );
  const extendedResult = useExtendedBackfillMetricsStore((s) => s.result);
  const extendedSyncType = useExtendedBackfillMetricsStore((s) => s.syncType);
  const prevErrorRef = useRef<string | null>(null);
  const completionHandledRef = useRef(false);

  // Sync errors when session matches
  useEffect(() => {
    if (
      !onboardingSessionId ||
      extendedSessionId !== onboardingSessionId ||
      extendedError === prevErrorRef.current
    ) {
      return;
    }
    prevErrorRef.current = extendedError;
    if (extendedError) {
      setOnboardingError(extendedError);
    }
  }, [
    onboardingSessionId,
    extendedSessionId,
    extendedError,
    setOnboardingError,
  ]);

  // On successful completion: capture result, call setInternalOnboardingTeamInsights with done: 1
  useEffect(() => {
    if (
      status !== 'authenticated' ||
      !onboardingSessionId ||
      !onboardingOrgUuid ||
      extendedSessionId !== onboardingSessionId ||
      !extendedIsCompleted ||
      extendedSyncType !== 'backfill' ||
      completionHandledRef.current
    ) {
      return;
    }
    completionHandledRef.current = true;
    markOnboardingCompleted(extendedResult);
    setTeamInsightsOnboardingComplete({
      OrgUUID: onboardingOrgUuid,
      done: 1,
    }).catch(() => {});
  }, [
    status,
    onboardingSessionId,
    onboardingOrgUuid,
    extendedSessionId,
    extendedIsCompleted,
    extendedSyncType,
    extendedResult,
    markOnboardingCompleted,
  ]);

  return null;
}
